import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sparkles, ArrowUpRight } from "lucide-react";
import Reveal from "../Reveal";
import { FAMILY_HERO, PRODUCT_LINE, CHARGING } from "../../lib/brand";

const ProductCard = ({ p, i }) => (
  <Reveal delay={i * 0.1} y={50}>
    <div
      data-testid={`product-${p.brand.toLowerCase().replace(/\s/g, "-")}`}
      className="group relative flex h-full flex-col overflow-hidden rounded-sm border border-white/10 bg-[#0a0a0a]"
    >
      <div className="relative h-72 overflow-hidden">
        <img
          src={p.image}
          alt={`${p.brand} bottle`}
          className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        <span
          className="absolute left-5 top-5 rounded-full border px-3 py-1 font-mono text-[10px] tracking-[0.3em] backdrop-blur-md"
          style={{
            color: p.accent,
            borderColor: `${p.accent}55`,
            background: "rgba(0,0,0,0.4)",
          }}
        >
          {p.stage}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-7">
        <h3 className="font-serif text-3xl md:text-4xl text-zinc-50">{p.brand}</h3>
        <p
          className="mt-2 font-mono text-[11px] tracking-[0.15em]"
          style={{ color: p.accent }}
        >
          {p.tagline}
        </p>
        <p className="mt-4 flex-1 text-sm font-light leading-relaxed text-zinc-400">
          {p.desc}
        </p>
        <div className="mt-6 flex flex-wrap gap-2">
          {p.sizes.map((s) => (
            <span
              key={s}
              className="rounded-full border border-white/15 px-3 py-1 font-mono text-[10px] tracking-[0.15em] text-zinc-300"
            >
              {s.toUpperCase()}
            </span>
          ))}
        </div>
      </div>
    </div>
  </Reveal>
);

export const Family = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="family" data-testid="family-section" className="bg-[#030303] py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        {/* Header */}
        <Reveal className="max-w-3xl">
          <span className="font-mono text-[11px] tracking-[0.4em] text-teal-300">
            THE COLLECTION
          </span>
          <h2 className="mt-6 font-serif font-light tracking-tighter text-4xl md:text-6xl lg:text-7xl leading-[1] text-zinc-100">
            The Waterboat <span className="italic text-teal-300">Family</span>
          </h2>
          <p className="mt-6 text-lg font-light text-zinc-400">
            One journey, three stages. Every bottle by Watermelon Foods &amp;
            Beverages begins with a story, moves with aspiration, and rises into
            abundance — and each is charged spiritually before it reaches you.
          </p>
        </Reveal>

        {/* Reference group image */}
        <Reveal className="mt-14" y={60}>
          <div
            ref={ref}
            className="relative h-[46vh] md:h-[70vh] overflow-hidden rounded-sm border border-white/10"
          >
            <motion.img
              style={{ y: imgY }}
              src={FAMILY_HERO}
              alt="The full Manifest with Waterboat product line-up"
              className="absolute inset-0 h-[115%] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[10px] md:text-[11px] tracking-[0.3em] text-zinc-300">
              <span className="text-[#6b8cff]">BELIEF</span>
              <span className="text-white/30">→</span>
              <span className="text-teal-300">ASPIRATION</span>
              <span className="text-white/30">→</span>
              <span className="text-[#f5c542]">ABUNDANCE</span>
            </div>
          </div>
        </Reveal>

        {/* Product cards */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {PRODUCT_LINE.map((p, i) => (
            <ProductCard key={p.brand} p={p} i={i} />
          ))}
        </div>

        {/* 20L Jars band */}
        <Reveal className="mt-6" y={40}>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-0 overflow-hidden rounded-sm border border-white/10 bg-[#0a0a0a]">
            <div className="md:col-span-2 relative h-64 md:h-auto overflow-hidden">
              <img
                src="/family/jar.png?v=2"
                alt="Waterboat 20L jar on a mountain stream"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="md:col-span-3 p-8 md:p-12 flex flex-col justify-center">
              <span className="font-mono text-[10px] tracking-[0.3em] text-teal-300">
                PURPOSE · 20L JARS
              </span>
              <h3 className="mt-4 font-serif text-3xl md:text-5xl text-zinc-50">
                One solution. Endless uses.
              </h3>
              <p className="mt-4 max-w-xl text-sm font-light leading-relaxed text-zinc-400">
                For homes, offices, clinics, gyms, cafés and events — large-format
                20L jars deliver consistent, hygienically sealed purity in every
                pour. Available across Waterboat and Bottled Stories.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Charging note */}
        <Reveal className="mt-16" y={30}>
          <div className="flex flex-col items-center text-center">
            <span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.35em] text-teal-300">
              <Sparkles size={14} /> ULTIMATELY, ALL CHARGED SPIRITUALLY
            </span>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              {CHARGING.map((c, i) => (
                <span key={c} className="flex items-center gap-6">
                  {i > 0 && <span className="hidden md:inline text-teal-400/50">✦</span>}
                  <span className="font-serif italic text-xl md:text-2xl text-zinc-200">
                    {c}
                  </span>
                </span>
              ))}
            </div>
            <a
              href="https://www.instagram.com/manifestwithwaterboat/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="family-journal-cta"
              className="group mt-10 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-zinc-300 hover:text-teal-300 transition-colors"
            >
              ASK FOR THE MANIFESTATION JOURNAL
              <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Family;
